import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import { AuthProvider } from './auth/AuthContext';
import './styles.css';

// Keep serving cached data while offline instead of retrying into a void.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount) => navigator.onLine && failureCount < 2,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      networkMode: 'offlineFirst',
    },
  },
});

// The shell that paints on launch comes out of the precache, so the app you
// see right after a deploy is always the one the *old* worker cached. The
// autoUpdate reload only fires once a new worker activates, and registerSW
// asks the server for one just once, at page load -- which a standalone window
// resumed from the app switcher never does. Ask again every time the app comes
// back to the foreground, so a resume behaves like a cold start.
registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    document.addEventListener('visibilitychange', () => {
      // update() rejects offline, and rejecting here would go unhandled.
      if (document.visibilityState === 'visible' && navigator.onLine) {
        void registration.update().catch(() => {});
      }
    });
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
